import { useSettingsStore } from '@/stores';
import { DollarSign, Percent, Coins, MessageSquare } from 'lucide-react';
import { getCurrencySymbol } from '@/utils/currency';
import type { CurrencyCode, CurrencyDisplayMode, MessagePriceMode, MessagePriceFormat } from '@/types/settings';

export function RateConfig() {
  const {
    currencyDisplayMode, currencyCode, manualRate, recargo, showBsPrice, showLocalBsRate,
    historicalRateFillMode,
    messagePriceMode, messagePriceFormat,
    setSetting,
  } = useSettingsStore();

  const sym = getCurrencySymbol(currencyCode);

  return (
    <div className="space-y-4">
      {/* 1. Conversión de Moneda Base */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4">
        <div className="flex items-center gap-2">
          <DollarSign size={16} className="text-brand-primary" />
          <h3 className="text-sm font-semibold text-text-primary">Conversión a Bolívares (Bs.)</h3>
        </div>

        {/* Moneda Base */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-text-primary block">Moneda de Referencia</label>
          <div className="flex gap-2">
            {(['USD', 'EUR'] as CurrencyCode[]).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setSetting('currencyCode', code)}
                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-medium border transition-colors ${
                  currencyCode === code
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'bg-surface-secondary text-text-secondary border-border'
                }`}
              >
                {code === 'USD' ? 'Dólares ($ USD)' : 'Euros (€ EUR)'}
              </button>
            ))}
          </div>
        </div>

        {/* Tasa de Cambio Mode */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-text-primary block">Modo de Tasa de Cambio</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSetting('currencyDisplayMode', 'auto')}
              className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-medium border transition-colors ${
                currencyDisplayMode === 'auto'
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-surface-secondary text-text-secondary border-border'
              }`}
            >
              Oficial BCV ({currencyCode})
            </button>
            <button
              type="button"
              onClick={() => setSetting('currencyDisplayMode', 'manual' as CurrencyDisplayMode)}
              className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-medium border transition-colors ${
                currencyDisplayMode === 'manual'
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-surface-secondary text-text-secondary border-border'
              }`}
            >
              Manual
            </button>
          </div>
        </div>

        {currencyDisplayMode === 'manual' && (
          <div className="space-y-1.5 animate-fade-in">
            <label className="text-xs font-medium text-text-primary block">
              Tasa Manual (Bs. por {currencyCode})
            </label>
            <input
              type="number"
              step="0.01"
              value={manualRate || ''}
              onChange={(e) => setSetting('manualRate', parseFloat(e.target.value) || 0)}
              placeholder="Ej: 36.50"
              className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-lg text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>
        )}

        {/* Recargo % */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-text-primary flex items-center gap-1">
              <Percent size={12} />
              <span>Recargo Adicional (%)</span>
            </label>
            <span className="text-xs font-bold text-brand-primary">{recargo}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="30"
            step="1"
            value={recargo}
            onChange={(e) => setSetting('recargo', parseInt(e.target.value, 10))}
            className="w-full accent-brand-primary cursor-pointer"
          />
        </div>

        {/* Toggle Bs display */}
        <div className="flex items-center justify-between text-xs pt-2 border-t border-border-light">
          <div>
            <span className="font-medium text-text-primary">Mostrar conversión en Bs. en tarjetas</span>
            <p className="text-[11px] text-text-muted">Calcula automáticamente el equivalente en Bolívares</p>
          </div>
          <input
            type="checkbox"
            checked={showBsPrice}
            onChange={(e) => setSetting('showBsPrice', e.target.checked)}
            className="w-4 h-4 rounded text-brand-primary focus:ring-brand-primary/30 cursor-pointer accent-brand-primary"
          />
        </div>

        {/* Toggle rate used */}
        <div className="flex items-center justify-between text-xs">
          <div>
            <span className="font-medium text-text-primary">Mostrar tasa y fecha usada</span>
            <p className="text-[11px] text-text-muted">Indica la tasa de cambio empleada para el cálculo</p>
          </div>
          <input
            type="checkbox"
            checked={showLocalBsRate}
            onChange={(e) => setSetting('showLocalBsRate', e.target.checked)}
            className="w-4 h-4 rounded text-brand-primary focus:ring-brand-primary/30 cursor-pointer accent-brand-primary"
          />
        </div>

        {/* Modo de Relleno para Fines de Semana y Feriados */}
        <div className="space-y-1.5 pt-2 border-t border-border">
          <label className="text-xs font-medium text-text-primary block">
            Tasa aplicable en fines de semana y feriados
          </label>
          <p className="text-[11px] text-text-muted">
            Define qué tasa usar si no hubo cotización oficial en la fecha de la cita (ej. domingos o feriados bancarios)
          </p>
          <select
            value={historicalRateFillMode || 'next'}
            onChange={(e) => setSetting('historicalRateFillMode', e.target.value as 'next' | 'previous')}
            className="w-full bg-surface-secondary border border-border rounded-xl px-3 py-2 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
          >
            <option value="next">Siguiente día hábil más cercano (Recomendado)</option>
            <option value="previous">Día hábil anterior más cercano</option>
          </select>
        </div>
      </div>

      {/* 2. Precios en Mensajes de WhatsApp */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} className="text-brand-primary" />
          <h3 className="text-sm font-semibold text-text-primary">Precios en Mensajes de WhatsApp</h3>
        </div>

        {/* Inclusión de Precios */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-text-primary block">Inclusión de Montos</label>
          <select
            value={messagePriceMode}
            onChange={(e) => setSetting('messagePriceMode', e.target.value as MessagePriceMode)}
            className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-lg text-xs text-text-primary cursor-pointer"
          >
            <option value="off">Desactivado (No incluir precios en el mensaje)</option>
            <option value="auto">Automático (Muestra total o saldo pendiente según corresponda)</option>
            <option value="total">Siempre Total (El total es...)</option>
            <option value="pending">Solo Pendiente (Te queda pendiente...)</option>
          </select>
        </div>

        {messagePriceMode !== 'off' && (
          <div className="space-y-1.5 animate-fade-in">
            <label className="text-xs font-medium text-text-primary flex items-center gap-1">
              <Coins size={12} className="text-brand-primary" />
              <span>Formato de Moneda en Mensajes</span>
            </label>
            <select
              value={messagePriceFormat}
              onChange={(e) => setSetting('messagePriceFormat', e.target.value as MessagePriceFormat)}
              className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-lg text-xs text-text-primary cursor-pointer"
            >
              <option value="both">Ambas Monedas ({sym}25.00 / Bs. 985.00)</option>
              <option value="currency">Solo Moneda Original ({sym}25.00)</option>
              <option value="bs">Solo Bolívares (Bs. 985.00)</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
